import ReactDOM from 'react-dom';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import OKX_ICON from '../assets/images/okx_wallet.svg';
import TON_2 from '../assets/images/ton_2.svg';
import TON_KEEPER from '../assets/images/tonkeeper.svg';
import TON_3 from '../assets/images/ton_3.svg';
import TON_4 from '../assets/images/ton_4.svg';
import TOMO_ICON from '../assets/images/tomo.svg';
import ArrowRight from '../assets/images/arrow_right.svg';
import IconTG from '../assets/images/icon_tg.svg';
import UXUY_ICON from '../assets/images/uxuy.svg';
import { cn, validEmail } from '../util';
import useOkxTonConnect from '../hooks/useOkxTonConnect';
import useOkxEVMConnect from '../hooks/useOkxEVMConnect';
import useBitget from '../hooks/useBitget';
import { ChainIdWithConnectsMap } from '../util/const';
import { CONNECT_MAP } from '../util/const';
import useTomoConnect from '../hooks/useTomoConnect';
import {
  ConnectOptions,
  ConnectResult,
  TomoContext,
  TomoContextType,
} from '../TomoContext';
import {
  isPcBrowser,
  isIOS as detectIsIOS,
  getDisplayName,
  getDisplayDescription,
} from '../v2/utils/utils';
import { TomoOptions } from '../v2/types/types';
import useUxuyEVMConnect from '../hooks/useUxuyEVMConnect';

async function disconnectAll(providers: TomoContextType['providers']) {
  try {
    providers.tomo_ton?.disconnect?.();
    await providers.ethereum?.disconnect?.(); // do not disconnect for evm, it's async
    providers.tomo_sol?.disconnect?.(); // todo
  } catch (e) {
    console.error(e);
    console.error('caught error disconnecting, skipping');
  }
}

const TON_ICON = () => (
  <div className="tm-flex tm-flex-col tm-bg-[#F5F5FA] dark:tm-bg-[#2e2e30] tm-rounded-lg tm-p-[3px] tm-gap-[3px]">
    <div className="tm-flex tm-gap-[3px]">
      <TON_KEEPER />
      <TON_2 />
    </div>
    <div className="tm-flex tm-gap-[3px]">
      <TON_3 />
      <TON_4 />
    </div>
  </div>
);

const ICON_MAP = {
  [CONNECT_MAP.OKX_CONNECT]: OKX_ICON,
  [CONNECT_MAP.TON_CONNECT]: TON_ICON,
  [CONNECT_MAP.TOMO_MINI_APP]: TOMO_ICON,
  [CONNECT_MAP.BITGET_WALLET]: () => {
    return (
      <img
        style={{ height: 48, width: 48, borderRadius: 8 }}
        src="https://d13t1x9bdoguib.cloudfront.net/static/bitget-new.png"
      />
    );
  },
  [CONNECT_MAP.UXUY_WALLET]: () => (
    <div style={{ height: 48, width: 48, borderRadius: 8, backgroundColor: '#FF7400' }}>
      <UXUY_ICON />
    </div>
  ),
};

interface IProps {
  defaultSelect?: string;
  closeModal: () => void;
  show: boolean;
  setIsLoading: React.Dispatch<React.SetStateAction<boolean>>;
  manifestUrl?: string;
  setConnected: (connect: boolean) => void;
  chain: string;
  setProviders: (providers: Record<string, any>) => void;
  supportedConnects: CONNECT_MAP[];
  tomoOptions?: TomoOptions;
  theme?: 'dark' | 'light';
  connectOptions?: ConnectOptions;
  setConnectResult?: (result: ConnectResult) => void;
}

const ConnectMain = ({
  closeModal,
  show,
  setConnected,
  chain,
  manifestUrl,
  supportedConnects,
  tomoOptions = {} as any, // TODO
  theme,
  connectOptions,
  setIsLoading,
  setConnectResult,
}: IProps) => {
  const [email, setEmail] = useState('');
  const [isFocus, setIsFocus] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [showConnectors, setShowConnectors] = useState(true);
  const [isIOS, setIsIOS] = useState(false);
  const { providers, setProviders } = useContext(TomoContext);
  const {
    connect: okxTonConnect,
    connected: okxTonConnected,
  } = useOkxTonConnect({
    setProviders,
    chain,
    metaData: tomoOptions?.metaData,
    theme,
    setConnectResult,
    connectSuccess: closeModal,
  });
  const {
    connect: okxEVMConnect,
    connected: okxEVMConnected,
  } = useOkxEVMConnect({
    chain,
    metaData: tomoOptions?.metaData,
    theme,
  });

  const { connect: connectBitget } = useBitget({
    metaData: tomoOptions?.metaData,
  });

  const { connect: connectUxuy } = useUxuyEVMConnect({
    metaData: tomoOptions?.metaData,
  });

  const [tonConnect, setTonConnect] = useState<any>({
    connect: null,
    connected: false,
  });

  const { connect: tomConnect, connected: tomoConnected } = useTomoConnect({
    chain,
    options: tomoOptions,
    setProviders,
    setConnectResult,
  });

  const connectOptionList = useMemo(() => {
    if (supportedConnects.length) {
      return Array.from(
        new Set([CONNECT_MAP.TOMO_MINI_APP, ...supportedConnects])
      );
    }
    const connectedInHistory = window.localStorage.getItem(
      'connected_in_history'
    );
    const list = connectedInHistory
      ? ChainIdWithConnectsMap[chain] || []
      : [CONNECT_MAP.TOMO_MINI_APP];
    return list as CONNECT_MAP[];
  }, [chain, supportedConnects]);

  const connected = useMemo(
    () =>
      okxEVMConnected ||
      okxTonConnected ||
      tomoConnected ||
      tonConnect?.tonConnected,
    [okxTonConnected, okxEVMConnected, tomoConnected, tonConnect?.tonConnected]
  );

  useEffect(() => {
    setConnected(connected);
    if (connected) {
      window.localStorage.setItem('connected_in_history', '1');
      closeModal();
      setEmail('');
    }
  }, [connected]);

  const handleClickConnect = async (wallet: CONNECT_MAP) => {
    await disconnectAll(providers);
    // TODO: add other connect
    switch (wallet) {
      case CONNECT_MAP.OKX_CONNECT:
        setIsLoading(true);
        await handleOkxConnect();
        setIsLoading(false);
        break;
      case CONNECT_MAP.TON_CONNECT:
        // connect ton if already initialized
        if (tonConnect?.connect) {
          await tonConnect.connect(connectOptions);
          return;
        }

        // dynamically import TonConnect
        setIsLoading(true);
        const TonConnect = (await import('./TonConnect'))?.default;
        const TonConnectUIProvider = (await import('@tonconnect/ui-react'))
          ?.TonConnectUIProvider;
        const div = document.createElement('div');
        div.id = 'tomo-ton-connect';
        document.body.appendChild(div);
        ReactDOM.render(
          <TonConnectUIProvider
            manifestUrl={manifestUrl || './manifestUrl.json'}
          >
            <TonConnect
              closeModal={closeModal}
              setProviders={setProviders}
              setConnectResult={setConnectResult}
              updateTonConnect={setTonConnect}
            />
          </TonConnectUIProvider>,
          div,
          () => {
            setTimeout(() => {
              setIsLoading(false);
            }, 1000);
          }
        );
        break;
      case CONNECT_MAP.TOMO_MINI_APP:
        const res = await tomConnect(connectOptions);
        if (res) closeModal();
        break;
      case CONNECT_MAP.BITGET_WALLET: {
        await connectBitget();
        closeModal();
        break;
      }
      case CONNECT_MAP.UXUY_WALLET: {
        await connectUxuy();
        closeModal();
        break;
      }
      default:
        break;
    }
  };

  const handleOkxConnect = async () => {
    let res;
    if (chain === 'EVM') {
      res = await okxEVMConnect();
    } else if (chain === 'TON') {
      res = await okxTonConnect(connectOptions);
    }
    if (res) closeModal();
    return res;
  };

  const handleConnectTg = async () => {
    let res;
    if (!email) {
      res = await tomConnect(connectOptions);
    } else {
      res = !validEmail(email)
        ? await tomConnect(connectOptions)
        : await tomConnect({ ...connectOptions, email });
    }
    if (res) closeModal();
  };

  const handleEmailNextClick = () => {
    if (!validEmail(email)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
      tomConnect({ ...connectOptions, email });
    }
  };

  useEffect(() => {
    // if is not focused or is pc, then show connectors
    setShowConnectors(!isFocus || isPcBrowser());
  }, [isFocus]);
  useEffect(() => {
    // detect if is IOS
    setIsIOS(detectIsIOS());
  }, []);

  return (
    <div
      className={cn('tm-px-4 tm-mt-2 tm-mb-5 tm-select-none', {
        'tm-hidden': !show,
        'tm-h-[440px]': isFocus && isIOS,
      })}
    >
      <div
        className={cn(
          'tm-flex tm-items-center tm-justify-between tm-gap-2 tm-w-full tm-h-[48px] tm-border tm-rounded-[8px] tm-px-4 tm-py-[14px] dark:tm-bg-[#171717]',
          { 'tm-border-[#EBEBF4] dark:tm-border-[#171717]': !isFocus },
          { 'tm-border-[#12122A] dark:tm-border-[#FFF]': isFocus }
        )}
      >
        <input
          type="text"
          placeholder="Enter your email"
          className="tm-text-[14px] tm-border-none tm-outline-none tm-flex-1 tm-bg-transparent tm-text-[#12122A] dark:tm-text-white"
          value={email}
          onFocus={e => setIsFocus(true)}
          onBlur={e => setIsFocus(false)}
          onChange={async e => {
            setEmailError('');
            const v = e.target.value;
            setEmail(v);
          }}
        />
        <div
          className="tm-cursor-pointer tm-flex tm-justify-center tm-items-center"
          onTouchEnd={handleEmailNextClick}
          onClick={handleEmailNextClick}
        >
          <ArrowRight />
        </div>
      </div>
      {emailError && (
        <div className="tm-py-1 tm-text-[#EB4B6D] tm-text-[12px]">
          {emailError}
        </div>
      )}
      <div
        onTouchEnd={handleConnectTg}
        onClick={handleConnectTg}
        className="tm-mt-[10px] tm-select-none tm-flex tm-w-full tm-gap-2 tm-justify-center tm-items-center tm-h-[52px] tm-cursor-pointer tm-rounded-[8px] tm-bg-[#00A9FB] hover:tm-opacity-50"
      >
        <IconTG />
        <span className="tm-text-white tm-font-switzerMedium tm-text-[16px] tm-flex tm-gap-1">
          Continue with Telegram
        </span>
      </div>
      <div
        id="tomo-modal-connectors"
        className={cn({
          'tm-hidden': !showConnectors,
        })}
      >
        <div className="tm-flex tm-items-center tm-justify-between tm-gap-3 tm-mt-4 tm-mb-3 tm-select-none">
          <span className="tm-inline-block tm-flex-1 tm-h-[1px] tm-bg-[#EBEBF4] dark:tm-bg-[#444]"></span>
          <span className="tm-text-[#8989AB] tm-text-[14px]">
            or connect a wallet
          </span>
          <span className="tm-inline-block tm-flex-1 tm-h-[1px] tm-bg-[#EBEBF4] dark:tm-bg-[#444]"></span>
        </div>
        <div className="tm-flex tm-items-center tm-justify-center tm-pb-4 tm-gap-2">
          <span className="tm-flex">
            {connectOptionList.map(option => {
              const WalletIcon = ICON_MAP[option];
              const displayName = getDisplayName(option);
              const displayDesc = getDisplayDescription(option);
              return (
                <div
                  key={option}
                  className={cn(
                    'tm-flex tm-flex-1 tm-flex-col tm-gap-1 tm-items-center tm-justify-start tm-px-3'
                  )}
                >
                  <div
                    onClick={() => handleClickConnect(option)}
                    className="tm-flex tm-justify-start tm-items-center tm-flex-col tm-select-none"
                  >
                    <div className="tm-w-[56px] tm-h-[56px] tm-flex tm-items-center tm-justify-center">
                      <WalletIcon />
                    </div>
                    <span className="tm-text=[#12122A] tm-text-[12px] tm-font-switzerMedium tm-text-center tm-whitespace-nowrap">
                      {displayName}
                    </span>
                    <span className="tm-text-[#616184] tm-text-[10px] tm-font-switzerMedium tm-text-center tm-whitespace-nowrap">
                      {displayDesc}
                    </span>
                  </div>
                </div>
              );
            })}
          </span>
        </div>
      </div>
      <div
        id="tomo-modal-ios-placeholder"
        className={cn({
          // show if is IOS and connector is hidden
          'tm-hidden': !(isIOS && !showConnectors),
        })}
        style={{ height: 80 }}
      />
    </div>
  );
};

export default ConnectMain;
